import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Play, Pause, Flag, Edit } from "lucide-react";
import EnterResultModal from "./EnterResultModal";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return ""; }
}

export default function RaceManagementList({ group, refreshTick }) {
  const [dates, setDates] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [racesByDate, setRacesByDate] = useState({});
  const [expandedRace, setExpandedRace] = useState({});
  const [resultRace, setResultRace] = useState(null);

  const loadDates = useCallback(async () => {
    if (!group) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/meeting-dates`);
      setDates(data.meeting_dates);
    } catch (e) {}
  }, [group]);

  useEffect(() => { loadDates(); }, [loadDates, refreshTick]);

  const loadRaces = async (date) => {
    if (racesByDate[date]) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/races`, { params: { date } });
      setRacesByDate((p) => ({ ...p, [date]: data.races }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Load failed")); }
  };

  const toggleOpen = async (date) => {
    setExpanded((p) => ({ ...p, [date]: !p[date] }));
    if (!expanded[date]) await loadRaces(date);
  };

  const toggleStatus = async (race) => {
    const next = race.status === "open" ? "upcoming" : "open";
    try {
      const { data } = await api.patch(`/groups/${group.id}/races/${race.id}`, { status: next });
      setRacesByDate((p) => ({
        ...p,
        [race.meeting_date]: (p[race.meeting_date] || []).map((r) => r.id === race.id ? data.race : r),
      }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Update failed")); }
  };

  const toggleScratch = async (race, horse) => {
    try {
      const { data } = await api.patch(`/groups/${group.id}/races/${race.id}`, {
        horse_number: horse.horse_number, scratched: !horse.scratched,
      });
      setRacesByDate((p) => ({
        ...p,
        [race.meeting_date]: (p[race.meeting_date] || []).map((r) => r.id === race.id ? data.race : r),
      }));
      if (data.picks_auto_swapped > 0) {
        toast.success(`${data.picks_auto_swapped} pick${data.picks_auto_swapped === 1 ? "" : "s"} auto-swapped to favourite`);
      } else {
        toast.success(horse.scratched ? "Un-scratched" : "Scratched");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Update failed")); }
  };

  if (dates.length === 0) {
    return (
      <div className="bg-white border border-[#e9ecef] rounded-2xl p-6 text-center text-sm text-[#6c757d]" data-testid="race-mgmt-empty">
        No races loaded yet. Use "Load racing card" above.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="race-mgmt-list">
      {dates.map((d) => (
        <div key={d.meeting_date} className="bg-white border border-[#e9ecef] rounded-2xl overflow-hidden">
          <button
            onClick={() => toggleOpen(d.meeting_date)}
            className="w-full flex items-center justify-between px-4 py-3 bg-[#f8f9fa]"
            data-testid={`race-mgmt-date-${d.meeting_date}`}
          >
            <div className="text-left">
              <div className="font-display font-extrabold text-sm">{d.meeting_date}</div>
              <div className="text-[10px] text-[#6c757d] font-bold uppercase tracking-wider">
                {d.race_count} race{d.race_count === 1 ? "" : "s"} · {d.cutoff_passed ? "🔒 locked" : `cutoff ${fmtTime(d.cutoff)}`}
              </div>
            </div>
            {expanded[d.meeting_date] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expanded[d.meeting_date] && (
            <div className="p-2 space-y-2">
              {(racesByDate[d.meeting_date] || []).map((r) => {
                const scratchCount = r.horses.filter((h) => h.scratched).length;
                const rid = r.id;
                return (
                  <div key={rid} className="border border-[#e9ecef] rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-white">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-xs truncate">{r.track} · R{r.race_number} · {r.race_name}</div>
                        <div className="text-[10px] text-[#6c757d] font-bold">
                          {fmtTime(r.scheduled_time)} · {r.horses.length} runners{scratchCount ? ` · ${scratchCount} scratched` : ""}
                          {r.result && (
                            <span className="ml-1 text-[#2f9e44]">· Result: 🥇#{r.result.first_horse_number} 🥈#{r.result.second_horse_number} 🥉#{r.result.third_horse_number}</span>
                          )}
                        </div>
                      </div>
                      <span className={`text-[9px] font-black uppercase tracking-wider rounded-full px-1.5 py-0.5 ${
                        r.status === "open" ? "bg-[#2f9e44]/10 text-[#2f9e44] border border-[#2f9e44]/25" :
                        r.status === "finished" ? "bg-[#1971c2]/10 text-[#1971c2] border border-[#1971c2]/25" :
                        "bg-[#f8f9fa] text-[#6c757d] border border-[#e9ecef]"
                      }`}>{r.status}</span>
                      <button
                        onClick={() => setResultRace(r)}
                        data-testid={`race-mgmt-result-${rid}`}
                        className="rounded-lg px-2 py-1 text-[10px] font-bold border border-[#1971c2] text-[#1971c2] hover:bg-[#e7f0fb] flex items-center gap-1 active:scale-95 transition-transform"
                      >
                        {r.result ? <Edit className="w-3 h-3" strokeWidth={2.5} /> : <Flag className="w-3 h-3" strokeWidth={2.5} />}
                        {r.result ? "Edit" : "Result"}
                      </button>
                      {!r.result && (
                        <button
                          onClick={() => toggleStatus(r)}
                          data-testid={`race-mgmt-status-toggle-${rid}`}
                          className="rounded-lg px-2 py-1 text-[10px] font-bold border border-[#e9ecef] hover:bg-[#f8f9fa] flex items-center gap-1"
                        >
                          {r.status === "open" ? <Pause className="w-3 h-3" strokeWidth={2.5} /> : <Play className="w-3 h-3" strokeWidth={2.5} />}
                          {r.status === "open" ? "Close" : "Open"}
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedRace((p) => ({ ...p, [rid]: !p[rid] }))}
                        className="rounded-lg px-2 py-1 text-[10px] font-bold border border-[#e9ecef] hover:bg-[#f8f9fa]"
                        data-testid={`race-mgmt-expand-${rid}`}
                      >
                        {expandedRace[rid] ? "Hide" : "Horses"}
                      </button>
                    </div>
                    {expandedRace[rid] && (
                      <div className="p-2 space-y-1 bg-[#fafbfc]">
                        {r.horses.map((h) => (
                          <div key={h.horse_number} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-[#e9ecef]">
                            <span className="font-bold text-xs w-6 tabular-nums">{h.horse_number}</span>
                            <span className={`text-xs flex-1 truncate ${h.scratched ? "line-through text-[#6c757d]" : ""}`}>{h.name}</span>
                            <span className="text-[10px] text-[#6c757d] tabular-nums w-12 text-right">{h.odds.toFixed(2)}</span>
                            <button
                              onClick={() => toggleScratch(r, h)}
                              data-testid={`race-mgmt-scratch-${rid}-${h.horse_number}`}
                              className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider border transition-colors active:scale-95 ${
                                h.scratched
                                  ? "bg-[#c92a2a]/10 text-[#c92a2a] border-[#c92a2a]/25"
                                  : "bg-white text-[#6c757d] border-[#e9ecef] hover:border-[#c92a2a]"
                              }`}
                            >
                              {h.scratched ? "Scratched" : "Scratch"}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
      <EnterResultModal
        key={resultRace?.id}
        open={!!resultRace}
        onClose={() => setResultRace(null)}
        group={group}
        race={resultRace}
        onSaved={(updated) => {
          if (!updated) {
            // cleared
            setRacesByDate((p) => ({
              ...p,
              [resultRace.meeting_date]: (p[resultRace.meeting_date] || []).map((r) => r.id === resultRace.id ? { ...r, result: null, status: "open" } : r),
            }));
          } else {
            setRacesByDate((p) => ({
              ...p,
              [updated.meeting_date]: (p[updated.meeting_date] || []).map((r) => r.id === updated.id ? updated : r),
            }));
          }
        }}
      />
    </div>
  );
}
